'use client'

import { useWeather } from '@/lib/hooks/useWeather'

// Interface pour structurer nos données météo
interface WeatherCondition {
  label: string;
  day: string;
  night: string;
}

const WEATHER_MAP: Record<number, WeatherCondition> = {
  0: { label: 'Ciel clair', day: '☀️', night: '🌙' },
  1: { label: 'Principalement clair', day: '🌤️', night: '✨' },
  2: { label: 'Partiellement nuageux', day: '⛅', night: '☁️' },
  3: { label: 'Couvert', day: '☁️', night: '☁️' },
  45: { label: 'Brouillard', day: '🌫️', night: '🌫️' },
  48: { label: 'Brouillard givrant', day: '🌫️❄️', night: '🌫️❄️' },
  51: { label: 'Bruine légère', day: '🌦️', night: '🌧️' },
  53: { label: 'Bruine modérée', day: '🌦️', night: '🌧️' },
  55: { label: 'Bruine dense', day: '🌦️', night: '🌧️' },
  56: { label: 'Bruine verglaçante légère', day: '🌧️❄️', night: '🌧️❄️' },
  57: { label: 'Bruine verglaçante dense', day: '🌧️❄️', night: '🌧️❄️' },
  61: { label: 'Pluie faible', day: '🌧️', night: '🌧️' },
  63: { label: 'Pluie modérée', day: '🌧️', night: '🌧️' },
  65: { label: 'Pluie forte', day: '🌧️', night: '🌧️' },
  66: { label: 'Pluie verglaçante légère', day: '🧊🌧️', night: '🧊🌧️' },
  67: { label: 'Pluie verglaçante forte', day: '🧊🌧️', night: '🧊🌧️' },
  71: { label: 'Neige faible', day: '🌨️', night: '🌨️' },
  73: { label: 'Neige modérée', day: '🌨️', night: '🌨️' },
  75: { label: 'Neige forte', day: '❄️', night: '❄️' },
  77: { label: 'Grains de neige', day: '🌨️', night: '🌨️' },
  80: { label: 'Averses légères', day: '🌦️', night: '🌧️' },
  81: { label: 'Averses modérées', day: '🌦️', night: '🌧️' },
  82: { label: 'Averses violentes', day: '🌧️', night: '🌧️' },
  85: { label: 'Averses de neige légères', day: '🌨️', night: '🌨️' },
  86: { label: 'Averses de neige fortes', day: '🌨️', night: '🌨️' },
  95: { label: 'Orage', day: '⛈️', night: '⛈️' },
  96: { label: 'Orage avec grêle', day: '⛈️🌨️', night: '⛈️🌨️' },
  99: { label: 'Orage fort', day: '⚡', night: '⚡' },
}

export default function WeatherWidget() {
  const { data, error, loading } = useWeather()

  if (loading) return (
    <div style={{
      padding: '4px 10px', borderRadius: '20px',
      background: 'var(--bg-panel)', border: '1px solid var(--border-dim)',
      fontSize: '12px', fontFamily: 'var(--font-mono)',
      color: 'var(--text-muted)', opacity: 0.5,
    }}>
      ···
    </div>
  )

  if (error || !data) return (
    <div style={{
      padding: '4px 10px', borderRadius: '20px',
      background: 'var(--bg-panel)', border: '1px solid var(--border-dim)',
      fontSize: '12px', color: 'var(--coral)',
    }}>
      ⚠️
    </div>
  )

  // Récupération de l'objet météo ou fallback
  const weatherInfo = WEATHER_MAP[data.weathercode]
  const isDay = data.is_day !== 0 // Open-Meteo renvoie 1 pour jour, 0 pour nuit
  
  const emoji = weatherInfo ? (isDay ? weatherInfo.day : weatherInfo.night) : '🌡️'
  const desc = weatherInfo?.label ?? 'Météo'

  return (
    <div
      title={`${data.city} — ${desc} · Vent: ${data.windspeed} km/h`}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '4px 10px', borderRadius: '20px',
        background: 'var(--bg-panel)', border: '1px solid var(--border-dim)',
        fontSize: '12px', fontFamily: 'var(--font-mono)',
        color: 'var(--text-secondary)',
        cursor: 'help', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: '15px' }}>{emoji}</span>
      <span style={{ fontWeight: 500 }}>{Math.round(data.temperature)}°C</span>
      <span style={{ opacity: 0.55, fontSize: '11px' }}>{data.city}</span>
    </div>
  )
}
